import { cn } from "@/core/helpers/cn";
import { ArrowLeftIcon } from "@/shared/icons";
import Button from "@/shared/ui/button/Button";

export function BankBranchPagination({
  value,
  totalPages,
  onChange,
}: {
  value: number;
  totalPages: number;
  onChange: (page: number) => void;
}) {
  // Ensure at least 1 page is displayed
  const displayPages = Math.max(1, totalPages);

  // Navigation state
  const isFirstPage = value === 1;
  const isLastPage = value === displayPages;
  const shouldDisableArrows = displayPages <= 1;

  // Generate page numbers with ellipsis
  const generatePageNumbers = (): (number | string)[] => {
    if (displayPages <= 5) {
      return Array.from({ length: displayPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [1];
    const currentPage = value;
    const total = displayPages;

    if (currentPage <= 3) {
      pages.push(2, 3, 4);
      if (total > 5) {
        pages.push("...");
      }
      pages.push(total);
    } else if (currentPage >= total - 2) {
      pages.push("...");
      pages.push(total - 3, total - 2, total - 1, total);
    } else {
      pages.push("...");
      pages.push(currentPage - 1, currentPage, currentPage + 1);
      pages.push("...");
      pages.push(total);
    }

    return pages;
  };

  const pageNumbers = generatePageNumbers();

  // Helper function to get button classes
  const getPageButtonClasses = (page: number | string) => {
    const baseClasses = "p-0 size-12 text-14 text-white bg-dark-800";

    if (page === "...") {
      return cn(baseClasses, "cursor-default");
    }

    if (page === value) {
      return baseClasses;
    }

    return cn(baseClasses, "hover:text-white/70 hover:bg-white/10 before:hidden");
  };

  // Helper function to get arrow classes
  const getArrowClasses = (isDisabled: boolean) => {
    return cn("size-3 text-white", isDisabled && "opacity-50");
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {pageNumbers.map((page, index) => (
          <Button
            key={page === "..." ? `ellipsis-${index}` : page}
            type="button"
            data-mz-accent="green"
            onClick={() => typeof page === "number" && onChange(page)}
            disabled={page === "..."}
            className={getPageButtonClasses(page)}
          >
            {page}
          </Button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          className="p-0 size-12 hover:text-white/70 hover:bg-white/10 bg-dark-800 before:hidden disabled:opacity-50 disabled:pointer-events-none"
          data-mz-accent="green"
          disabled={isFirstPage || shouldDisableArrows}
          onClick={() => onChange(value - 1)}
        >
          <ArrowLeftIcon className={getArrowClasses(isFirstPage || shouldDisableArrows)} />
        </Button>

        <Button
          type="button"
          className="p-0 size-12 hover:text-white/70 hover:bg-white/10 bg-dark-800 before:hidden disabled:opacity-50 disabled:pointer-events-none"
          data-mz-accent="green"
          disabled={isLastPage || shouldDisableArrows}
          onClick={() => onChange(value + 1)}
        >
          <ArrowLeftIcon
            className={cn(getArrowClasses(isLastPage || shouldDisableArrows), "rotate-180")}
          />
        </Button>
      </div>
    </div>
  );
}
